import app from 'app/app'
import ValueComposer from 'lib/compose/ValueComposer'
import { Interval } from 'types'
import * as _ from 'lodash'

interface TimepointComposerOptions {
	attachType?: boolean,
	attachNumber?: boolean,
	attachUnit?: boolean,
	attachVariance?: boolean,
	attachVarianceType?: boolean,
	attachRanges?: boolean,
	attachUnitOnRange?: boolean,
	canConvert?: any,
	timepointDesc?: string,
	timepointDescHigh?: string,
}

const DEFAULTS = {
	attachType: true,
	attachNumber: true,
	attachUnit: true,
	attachVariance: true,
	attachVarianceType: true,
	attachRanges: true,
	attachUnitOnRange: false,
	timepointDesc: '',
	timepointDescHigh: '',
}

const VARIANCE_TYPES = ['Average', 'Mean', 'Median']
const PREPEND_TYPES = ['Varies', 'During', 'Before', 'After']
const APPEND_TYPES = ['Duration']
const CONVERTABLE_UNITS = [
	'd',
	'wk',
	'mo',
	'yr',
]
const UNIT_VALUES = {
	yr: 365,
	mo: 30.4368,
	wk: 7,
	d: 1,
}

// Similar to the AttributeNameComposer and ValueComposer, this
//	is responsible for iterating over the properties of a timepoint
//	to construct a complete display string.
export default class TimepointComposer {

	static compose(timepoint: Interval, options?: TimepointComposerOptions): string {
		if (timepoint == null || typeof timepoint !== 'object')
			return ''

		options = Object.assign({}, DEFAULTS, options)

		let gestalt = ''

		// Certain types are prepended
		if (options.attachType && (PREPEND_TYPES.includes(timepoint.Type) || 
				VARIANCE_TYPES.includes(timepoint.Type) && !options.attachVarianceType)) {
			gestalt += timepoint.Type + ' ' + gestalt
		}

		// Check the user settings for a timepoint standardization
		// Attach to the options so that we can pass to other methods
		options.canConvert = app.user.TimepointUnit && app.user.TimepointUnit !== 'None' &&
			(timepoint.Unit || timepoint?.Low?.Unit || timepoint?.High?.Unit)

		if (timepoint.Operator && (timepoint.Number != null || timepoint.Value)) {
			if (gestalt)
				gestalt += ' '
			gestalt += timepoint.Operator
		}

		if (options.attachNumber && timepoint.Number ||
				(timepoint.Number != null && options.attachUnit && timepoint.Unit)) {
			if (gestalt)
				gestalt += ' '
			let displayNumber = timepoint.Number
			if (!timepoint.Multiplier && options.canConvert && needsUnitConversion(timepoint.Unit)) {
				displayNumber = convertUnitValue(timepoint.Number, timepoint.Unit)
			}
			gestalt += displayNumber
		}

		// Sometimes this is actually the whole timepoint, sometimes it pairs with Number
		if (options.attachUnit && timepoint.Unit) {
			if (timepoint.Multiplier) {
				let displayMultplier = timepoint.Multiplier
				if (options.canConvert && needsUnitConversion(timepoint.Unit))
					displayMultplier = convertUnitValue(timepoint.Multiplier, timepoint.Unit)
				if (gestalt)
					gestalt += ' '
				gestalt += 'every ' + displayMultplier
			}

			// It can also be redundant with the range units
			let displayUnit = timepoint.Unit
			if (options.canConvert && needsUnitConversion(timepoint.Unit))
				displayUnit = app.user.TimepointUnit
			if (!gestalt.trim().endsWith(displayUnit)) {
				if (gestalt)
					gestalt += ' '
				gestalt += displayUnit
			}
		} else if (timepoint.Value && timepoint.Value !== 'NA') {
			if (gestalt)
				gestalt += ' '
			gestalt += timepoint.Value
		}

		if (options.attachType && APPEND_TYPES.indexOf(timepoint.Type) > -1) {
			if (gestalt)
				gestalt += ' '
			gestalt += timepoint.Type
		}

		if (timepoint.Low || timepoint.High) {
			if (gestalt)
				gestalt += ' '
			gestalt += TimepointComposer.Range(timepoint, options)
		} else {
			if (options.timepointDesc) {
				if (gestalt)
					gestalt += ' '
				gestalt += options.timepointDesc
			}

			if (options.timepointDescHigh) {
				if (gestalt)
					gestalt += ' '
				gestalt += options.timepointDescHigh
			}

		}

		let hadVarianceValue
		if (options.attachVariance) {
			if (timepoint.SD != null) {
				hadVarianceValue = true
				if (gestalt)
					gestalt += ' '
				gestalt += '('
				if (options.attachVarianceType) {
					if (VARIANCE_TYPES.includes(timepoint.Type)) {
						gestalt += timepoint.Type + ', '
					}
					gestalt += 'SD '
				}
				let displaySD = timepoint.SD
				if (options.canConvert && needsUnitConversion(timepoint.Unit))
					displaySD = convertUnitValue(timepoint.SD, timepoint.Unit)
				gestalt += '± ' + displaySD + ')'
			}

			if (timepoint.SE != null) {
				hadVarianceValue = true
				if (gestalt)
					gestalt += ' '
				gestalt += '('
				if (options.attachVarianceType) {
					if (VARIANCE_TYPES.includes(timepoint.Type)) {
						gestalt += timepoint.Type + ', ' + gestalt
					}
					gestalt += 'SE '
				}
				let displaySE = timepoint.SE
				if (options.canConvert && needsUnitConversion(timepoint.Unit))
					displaySE = convertUnitValue(timepoint.SE, timepoint.Unit)
				gestalt += '± ' + displaySE + ')'
			}

			if (timepoint.Variance != null) {
				hadVarianceValue = true
				if (gestalt)
					gestalt += ' '
				gestalt += '('
				if (options.attachVarianceType) {
					if (VARIANCE_TYPES.includes(timepoint.Type)) {
						gestalt += timepoint.Type + ', ' + gestalt
					}
					gestalt += 'Unknown '
				}
				let displayVariance = timepoint.Variance
				if (options.canConvert && needsUnitConversion(timepoint.Unit))
					displayVariance = convertUnitValue(timepoint.Variance, timepoint.Unit)
				gestalt += '± ' + displayVariance + ')'
			}
		}
		if (options.attachVarianceType && !hadVarianceValue && VARIANCE_TYPES.includes(timepoint.Type)) {
			if (gestalt)
				gestalt += ' '
			gestalt += ' (' + timepoint.Type + ')'
		}

		if (options.attachRanges && timepoint.Ranges) {
			for (const range of timepoint.Ranges) {
				if (gestalt)
					gestalt += ' '
				gestalt += '['
				if (range.Type)
					gestalt += ValueComposer.RangeType(range)
				gestalt += TimepointComposer.Range(range, _.assign({
					attachUnit: options.attachUnitOnRange,
				}, options, {
					timepointDesc: false,
				}))
				gestalt += ']'
			}
		}

		return gestalt
	}

	// Ranges for timepoints are a strange beast - some of the logic here
	//	 is to deal with the fact that important data might be present but
	//	 shouldn't actually be rendered as a range
	static Range(range, options: TimepointComposerOptions = DEFAULTS): string {
		if (range == null || typeof range !== 'object')
			return ''

		// sometimes the whole timepoint is just a unit on the range :(
		if (range.Low && range.High && range.Low.Number === range.High.Number && !range.Low.Number) {
			if (range.Low.Unit != null && range.Low.Unit === range.High.Unit) {
				return range.Low.Unit
			} else if (range.Low.Value != null && range.Low.Value === range.High.Value) {
				return range.Low.Value
			}
		}

		let gestalt = ''
		if (range.Low) {
			if (range.Low.Operator)
				gestalt += range.Low.Operator
			if ((range.Low.Number != null && range.Low.Unit) || range.Low.Number) {
				let lowNumber = range.Low.Number
				if (options.canConvert && needsUnitConversion(range.Low.Unit))
					lowNumber = convertUnitValue(range.Low.Number, range.Low.Unit)
				gestalt += lowNumber
			}

			if (range.Low.Value) {
				if (range.Low.Number)
					gestalt += ' '
				gestalt += range.Low.Value
			} else if (options.attachUnit && range.Low.Unit) {
				if (range.Low.Number != null)
					gestalt += ' '
				let unit = range.Low.Unit
				if (options.canConvert && needsUnitConversion(range.Low.Unit))
					unit = app.user.TimepointUnit
				gestalt += unit
			}

			if (options.timepointDesc) {
				if (gestalt) gestalt += ' '
				gestalt += options.timepointDesc
			}
		}

		if (range.High) {
			if (range.Low && (range.Low.Number != null || range.Low.Value))
				gestalt += ' – '
			else if (gestalt)
				gestalt += ' '

			if (range.High.Operator)
				gestalt += range.High.Operator

			if ((range.High.Number != null && range.High.Unit) || range.High.Number) {
				let highNumber = range.High.Number
				if (options.canConvert && needsUnitConversion(range.High.Unit))
					highNumber = convertUnitValue(range.High.Number, range.High.Unit)
				gestalt += highNumber
			}

			if (range.High.Value) {
				if (range.High.Number)
					gestalt += ' '
				gestalt += range.High.Value
			} else if (options.attachUnit && range.High.Unit) {
				if (range.High.Number != null)
					gestalt += ' '
				let unit = range.High.Unit
				if (options.canConvert && needsUnitConversion(range.High.Unit))
					unit = app.user.TimepointUnit
				gestalt += unit
			}

			if (options.timepointDescHigh) {
				if (gestalt) gestalt += ' '
				gestalt += options.timepointDescHigh
			}
		}
		return gestalt
	}
}

const composeTimepoint = TimepointComposer.compose
export {
	composeTimepoint,
}

const needsUnitConversion = (unit?: string|undefined): boolean => {
	return unit && app.user.TimepointUnit !== unit && CONVERTABLE_UNITS.indexOf(unit) > -1
}

const convertUnitValue = (value, unit): number => {
	return parseFloat((value * UNIT_VALUES[unit] / UNIT_VALUES[app.user.TimepointUnit]).toFixed(2))
}
